import React, { Ref, useCallback, useImperativeHandle, FC, PropsWithRef, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, makeStyles, createStyles, Typography } from '@material-ui/core';
import { useCompactDialog } from '../../../../legacy-components/dialog/UseCompactDialog';
import { IdentityApplicationEnvironmentType } from './PublisherApplicationService';
import { ApplicationDeleteFormData } from './models/application.model';
import { FormattedMessage } from 'react-intl';
import ApplicationDeleteForm from './application-form/ApplicationDeleteForm';

const useStyles = makeStyles(({ spacing }) => {
    return createStyles({
        header: {
            position: "relative"
        },
        typeText: {
            padding: spacing(3, 0, 2, 0)
        }
    })
});

export interface IdentityApplicationDeleteDialogRef {
    open(): void
}

interface IdentityApplicationDeleteDialogProps {
    ref?: Ref<IdentityApplicationDeleteDialogRef | undefined>,
    applicationName: string;
    env: IdentityApplicationEnvironmentType,
    onSubmit: (data: ApplicationDeleteFormData) => void
}

interface ApplicationDeleteFormViewerProps {
    applicationName: string;
    env: IdentityApplicationEnvironmentType;
    onCancel: () => void;
    onSubmit: (data: ApplicationDeleteFormData) => void
}

const ApplicationDeleteFormContent = ({ applicationName }: {
    applicationName: string
}) => {
    const styles = useStyles();
    const applicationNameFormatter = () => <strong>{applicationName}</strong>;

    return (
        <>
            <Typography component="p">
                <FormattedMessage values={{
                    applicationName: applicationNameFormatter
                }} defaultMessage="This action cannot be undone. This will permanently remove the <applicationName>name</applicationName> and cannot be accessed elsewhere." />
            </Typography>

            <Typography className={styles.typeText} variant="body2" component="p">
                <FormattedMessage values={{
                    applicationName: applicationNameFormatter
                }} defaultMessage="Type <applicationName>name</applicationName> to confirm." />
            </Typography>
        </>
    )
}

const ApplicationDeleteFormViewer = ({ applicationName, env, onSubmit, onCancel }: ApplicationDeleteFormViewerProps) => {

    return (
        <ApplicationDeleteForm applicationName={applicationName} formContent={<ApplicationDeleteFormContent applicationName={applicationName} />} env={env} onCancel={onCancel} onSuccess={onSubmit} />
    )
}

const IdentityApplicationDeleteDialog: FC<PropsWithRef<IdentityApplicationDeleteDialogProps>> = forwardRef(({ applicationName, env, onSubmit }, ref) => {
    const style = useStyles();
    const { maxWidth, open, scroll, setOpen } = useCompactDialog();

    const handleClose = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    const handleSubmit = useCallback((data: ApplicationDeleteFormData) => {
        setOpen(false);
        onSubmit(data);
    }, [onSubmit, setOpen]);

    const handleClickOpen = () => {
        setOpen(true);
    };

    useImperativeHandle(ref, () => ({
        open: () => handleClickOpen()
    }));

    return (
        <Dialog
            fullWidth={true}
            maxWidth={maxWidth}
            open={open}
            scroll={scroll}
            onClose={handleClose}
            disableBackdropClick={true}
            disableEscapeKeyDown={true}
        >

            <div className={style.header}>
                <DialogTitle>
                    <FormattedMessage defaultMessage="Delete this application?" />
                </DialogTitle>
            </div>
            <ApplicationDeleteFormViewer applicationName={applicationName} env={env} onCancel={handleClose} onSubmit={handleSubmit} />
        </Dialog>
    );
});

IdentityApplicationDeleteDialog.propTypes = {
    applicationName: PropTypes.string.isRequired,
    env: PropTypes.oneOf<IdentityApplicationEnvironmentType>(["trimble-pre-prod", "trimble-prod"]).isRequired
}

export default IdentityApplicationDeleteDialog

